/**
 * A bundle's book record (part 24c, P1): the bundle's metadata and theme as stored when part 05 reads them, so a book comes
 * back exactly (L10), else a refused book record's (P11), else new ones; the title the writer gave over the metadata's only
 * when it differs. Its link to the library's templates (part 15e, TL17–TL20). Pure.
 */
import { DEFAULT_THEME, metadataFromJson, newMetadata, serializeMetadata, serializeTheme, THEME_PRESETS, themeFromJson } from '@cockatrice/layout';
import type { BookMetadata } from '@cockatrice/layout';
import type { BookPiece, BookTexts } from '@cockatrice/export/bundle';
import { isId } from '@cockatrice/storage';
import type { BookRecord, InvalidRecord, TemplateRecord } from '@cockatrice/storage';
import { cleanTitle } from '../shell/workspace.ts';

/** The first of `texts` that `read` takes, with what it reads as; null when none does. */
function firstRead<T>(texts: readonly string[], read: (text: string) => T): { readonly text: string; readonly value: T } | null {
  for (const text of texts) {
    try {
      return { text, value: read(text) };
    } catch {
      // P1: one part 05 refuses is passed over.
    }
  }
  return null;
}

/** The book's title as the review shows it: the metadata's when part 05 reads it, else `fallback` (the file's name). */
export function bundleTitle(metadata: string | null, fallback: string): string {
  return firstRead(metadata === null ? [] : [metadata], metadataFromJson)?.value.title ?? fallback;
}

/**
 * P1: book `bookId`'s record, made at `now`: the first metadata of `texts` part 05 reads (else new metadata titled
 * `untitled`), its text kept as stored unless `title` (cleaned as part 05 does titles; empty keeps it) differs from its
 * title; the first theme part 05 reads, as stored, else the default.
 */
export function bundleBook(bookId: string, texts: BookTexts, title: string, untitled: string, now: number): BookRecord {
  const metadata = firstRead(texts.metadata, metadataFromJson);
  const theme = firstRead(texts.theme, themeFromJson);
  const wanted = cleanTitle(title);
  const base: BookMetadata = metadata?.value ?? newMetadata(cleanTitle(untitled));
  const text = metadata !== null && (wanted === '' || wanted === base.title) ? metadata.text : serializeMetadata({ ...base, title: wanted === '' ? base.title : wanted });
  return { id: bookId, metadata: text, theme: theme?.text ?? serializeTheme(DEFAULT_THEME), template: null, createdAt: now };
}

/** TL17–TL20: what an imported book links to, and the template to create with it in the book's write; neither for none. */
export interface BundleLink {
  readonly link: string | null;
  readonly add: TemplateRecord | null;
}

/** TL20: no link, and nothing added. */
export const NO_LINK: BundleLink = Object.freeze({ link: null, add: null });

/** The library as the store holds it: every template it reads, and the key of each it refuses. */
export interface TemplateLibrary {
  readonly records: readonly TemplateRecord[];
  readonly invalid: readonly InvalidRecord[];
}

/** TL18: `text` as part 05 reads and writes it, the form themes compare in; null when it refuses it. */
function comparable(text: string): string | null {
  const read = firstRead([text], themeFromJson);
  return read === null ? null : serializeTheme(read.value);
}

/**
 * TL17–TL20 (principle 3): what a book of a bundle linked to `template`, with theme text `theme`, links to in `library`:
 *
 * | Row | The link | Outcome |
 * |---|---|---|
 * | TL20 | none, or the theme unreadable | no link, nothing added |
 * | TL18 | a built-in or a template here, the same theme | linked |
 * | TL19 | a built-in or a template here, another theme (one here that is unreadable or refused is one) | a new template of a fresh ID, linked |
 * | TL17 | an ID absent here | added under it, linked |
 * | TL17′ | absent here, and no ID a template record can hold (a forged bundle, or a built-in of a later build) | added under a fresh ID, linked |
 *
 * An added template takes the bundle's name and `theme` as stored, so it and the book hold one text. Nothing here changes
 * a template or any other book.
 */
export function bundleLink(template: BookPiece['template'], theme: string | undefined, library: TemplateLibrary, newId: () => string, now: number): BundleLink {
  const wanted = theme === undefined ? null : comparable(theme);
  if (template === null || theme === undefined || wanted === null) return NO_LINK;
  const { id, name } = template;
  const added = (key: string): BundleLink => ({ link: key, add: { id: key, name, theme, favourite: false, createdAt: now } });
  const preset = Object.entries(THEME_PRESETS).find(([key]) => key === id)?.[1];
  const here = preset === undefined ? library.records.find((record) => record.id === id)?.theme : serializeTheme(preset);
  if (here !== undefined) return comparable(here) === wanted ? { link: id, add: null } : added(newId());
  return isId(id) && !library.invalid.some((record) => record.key === id) ? added(id) : added(newId());
}
